import { useState, useEffect, useRef } from "react";
const IDLE_TIMEOUT = 60_000; // 1 minute of no interaction → pause polling
const ACTIVITY_EVENTS = ["click", "keydown", "scroll", "mousemove", "touchstart"];

/**
 * Returns a polling interval that becomes `0` (disabled) when:
 * - the browser tab is hidden, OR
 * - the user has not interacted for `IDLE_TIMEOUT` ms.
 *
 * When the tab is visible and the user is active, returns `baseInterval`.
 * Pair with `refetchOnFocus: true` so data refreshes instantly on tab return.
 */
export function useSmartPollingInterval(baseInterval) {
  const [active, setActive] = useState(() => typeof document !== "undefined" ? !document.hidden : true);
  const lastActivity = useRef(Date.now());
  const idleTimer = useRef(null);
  useEffect(() => {
    if (typeof document === "undefined") return;

    // ── Visibility change ──
    const onVisibility = () => {
      if (document.hidden) {
        setActive(false);
        if (idleTimer.current) {
          clearTimeout(idleTimer.current);
          idleTimer.current = null;
        }
      } else {
        // Tab became visible — mark active, restart idle timer
        lastActivity.current = Date.now();
        setActive(true);
        resetIdleTimer();
      }
    };

    // ── User interaction ──
    const onActivity = () => {
      lastActivity.current = Date.now();
      if (!document.hidden) {
        setActive(true);
        resetIdleTimer();
      }
    };
    function resetIdleTimer() {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        setActive(false);
      }, IDLE_TIMEOUT);
    }
    document.addEventListener("visibilitychange", onVisibility);
    for (const evt of ACTIVITY_EVENTS) {
      document.addEventListener(evt, onActivity, {
        passive: true
      });
    }

    // Start initial idle timer
    resetIdleTimer();
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      for (const evt of ACTIVITY_EVENTS) {
        document.removeEventListener(evt, onActivity);
      }
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, []);
  return active ? baseInterval : 0;
}